/**
 * Formatting shared by every gap-review view.
 *
 * The mockup carried pre-baked display strings (`shortDate: '30 Jun 2025'`)
 * because it had no dates to format. The API sends real timestamps, which arrive
 * as ISO strings even though the response type calls them `Date` — nothing in
 * the client parses JSON dates — so every helper here takes `Date | string`.
 *
 * Dates are formatted `en-GB` rather than through i18n on purpose: this surface
 * is English-only (see the layout route), and a reviewer reading pipeline field
 * names wants an unambiguous `30 Jun 2025`, not a locale-shifting one.
 */

const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** `30 Jun 2025`, or an em dash when the value is absent or unparseable. */
export function shortDate(value: Date | string | undefined): string {
  const date = toDate(value);
  return date ? SHORT_DATE_FORMATTER.format(date) : '—';
}

/** `30 Jun 2025, 17:04`, on the same absent-value rule as {@link shortDate}. */
export function dateTime(value: Date | string | undefined): string {
  const date = toDate(value);
  return date ? DATE_TIME_FORMATTER.format(date) : '—';
}

function toDate(value: Date | string | undefined): Date | null {
  if (value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * `SCOPE_OR_COMMITMENT_DRIFT` → `Scope or commitment drift`.
 *
 * The pipeline's enums are SCREAMING_SNAKE and appear both raw (in the pipeline
 * zone, where the field name is the point) and humanised (in the product zone).
 */
export function titleCase(enumValue: string): string {
  const spaced = enumValue.toLowerCase().replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Splits authored prose on blank lines. Single newlines stay inside a block. */
export function splitParagraphs(text: string): string[] {
  return text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0);
}

/**
 * `164.1k` — token counts, which run to six figures and are only ever read as an
 * order of magnitude. Absent stays an em dash rather than becoming a zero: a run
 * that never reported the number did not report zero of them.
 */
export function tokenCount(value: number | undefined): string {
  if (value === undefined) return '—';
  if (value < 1000) return String(value);
  return `${(value / 1000).toFixed(1)}k`;
}

/** `13m 46s` / `4.2s`, on the same absent-value rule as {@link tokenCount}. */
export function duration(milliseconds: number | undefined): string {
  if (milliseconds === undefined) return '—';
  const seconds = milliseconds / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

/** `1 comment` / `2 comments` — the only plural this surface needs. */
export function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}
